// Phase 5Z.9 — Fahrer Dashboard
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Truck, ChevronRight, Calendar, AlertTriangle } from "lucide-react";
import { useFahrerAuth } from "./lib/auth";
import { fahrerApi } from "./lib/api";
export default function FahrerDashboard() {
  const { me } = useFahrerAuth();
  const { t } = useTranslation();
  const [active, setActive] = useState(undefined);

  useEffect(() => {
    fahrerApi.get("/fahrer/active-handover")
      .then(({ data }) => setActive(data))
      .catch(() => setActive(null));
  }, []);

  if (!me) return null;
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-5" data-testid="fahrer-dashboard">
      <div>
        <p className="text-xs uppercase tracking-wider text-zinc-500">{t("dashboard.today")}</p>
        <p className="text-xs text-zinc-300 capitalize mt-0.5">{today}</p>
        <h1 className="text-xl font-bold text-zinc-50 mt-2">{t("dashboard.welcome", { name: me.first_name })}</h1>
      </div>

      <DashboardHelp/>

      {/* Phase 5Z.36 Phase 3 — Mein HR (Resturlaub, Überstunden, Krank) */}
      <FahrerHrWidget />

      {/* Vehicle / handover card */}
      {active === undefined && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 animate-pulse">
          <div className="h-4 bg-zinc-800 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-zinc-800 rounded w-1/3"></div>
        </div>
      )}
      {active === null && (
        <Link
          to="/fahrer/vehicle"
          data-testid="fahrer-dash-no-vehicle"
          className="block bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 hover:bg-amber-500/15 transition-colors"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-bold text-zinc-50">{t("handover.no_active")}</h3>
              <p className="text-xs text-zinc-300 mt-0.5">{t("handover.no_active_hint")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-400" />
          </div>
        </Link>
      )}
      {active && active.status === "draft" && (
        <Link
          to="/fahrer/vehicle"
          data-testid="fahrer-dash-draft-handover"
          className="block bg-amber-500/10 border border-amber-500/30 rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <div className="flex-1">
              <h3 className="text-sm font-bold text-zinc-50">Übernahme nicht abgeschlossen</h3>
              <p className="text-xs text-zinc-300 mt-0.5">Bitte Walkaround-Fotos und Unterschrift vervollständigen.</p>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-400" />
          </div>
        </Link>
      )}
      {active && active.status === "finalized" && (
        <Link
          to="/fahrer/vehicle"
          data-testid="fahrer-dash-active-vehicle"
          className="block bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 hover:bg-emerald-500/15 transition-colors"
        >
          <div className="flex items-start gap-3">
            <Truck className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-emerald-300/80">{t("handover.active_uebernahme")}</div>
              <div className="font-mono text-base font-bold text-zinc-50 mt-0.5">{active.vehicle_label}</div>
              <div className="text-xs text-zinc-400 mt-0.5">KM: <span className="font-mono">{(active.km || 0).toLocaleString()}</span></div>
            </div>
            <ChevronRight className="w-4 h-4 text-emerald-400" />
          </div>
        </Link>
      )}

      {/* Today's shift placeholder — wired in 5Z.11 */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid="fahrer-active-shift-card">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold">{t("dashboard.active_shift")}</h3>
        </div>
        <p className="text-xs text-zinc-500 italic">{t("dashboard.no_active_shift")}</p>
      </div>

      {/* Quick actions */}
      <div>
        <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">{t("dashboard.quick_actions")}</h3>
        <div className="space-y-2">
          <Link to="/fahrer/profile" data-testid="fahrer-quick-profile" className="flex items-center gap-3 px-4 py-3.5 bg-zinc-900 border border-zinc-800 rounded-lg hover:border-amber-600/50 transition-colors">
            <User className="w-5 h-5 text-amber-400" />
            <div className="flex-1"><div className="text-sm font-medium text-zinc-100">{t("dashboard.view_profile")}</div></div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </Link>
          <Link to="/fahrer/aufgaben" data-testid="fahrer-quick-aufgaben" className="flex items-center gap-3 px-4 py-3.5 bg-zinc-900 border border-zinc-800 rounded-lg hover:border-amber-600/50 transition-colors">
            <Calendar className="w-5 h-5 text-amber-400" />
            <div className="flex-1"><div className="text-sm font-medium text-zinc-100">Meine Aufgaben</div></div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </Link>
          {/* Lohnzettel quick-action hidden per user request — re-enable when feature is ready */}
        </div>
      </div>
    </div>
  );
}

function DashboardHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg" data-testid="fahrer-dashboard-help">
      <button onClick={() => setOpen(v => !v)} type="button" className="w-full px-3 py-2 text-left flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-300">ℹ Wie funktioniert das Portal?</span>
        <span className="text-[10px] text-amber-300/80">{open ? "Schließen" : "Öffnen"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 text-[11px] text-zinc-200 space-y-1.5">
          <p><b>Mein Fahrzeug</b>: Übernimm und gib dein Fahrzeug zurück (mit 8 Walkaround-Fotos).</p>
          <p><b>Zeiterfassung</b>: Erfasse jeden Arbeitstag mit Beginn / Ende, KM, Pausen und Unterschrift.</p>
          <p><b>Mein Plan</b>: Sieh deine Schichten, Stunden, gefahrene km und beantrage Korrekturen an Erfassungen.</p>
          <p><b>Aufgaben</b>: Aufgaben vom Büro — du musst sie annehmen oder ablehnen, bevor du weitermachen kannst.</p>
          <p><b>Glocken-Symbol oben</b>: deine Benachrichtigungen (Korrektur freigegeben/abgelehnt, neue Aufgabe).</p>
        </div>
      )}
    </div>
  );
}


// Phase 5Z.36 Phase 3 — Driver-facing HR snapshot
function FahrerHrWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fahrerApi.get("/fahrer/hr/widget").then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const v = data.vacation;
  const ot = data.overtime;
  const otNeg = ot.balance_minutes < 0;
  return (
    <Link to="/fahrer/hr" data-testid="fahrer-dashboard-hr-widget"
      className="block bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-emerald-700 transition">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Mein HR</div>
        <div className="flex items-center gap-2">
          {data.sick.currently_on_sick_leave && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40">
              Krankenstand
            </span>
          )}
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2" data-testid="fahrer-dashboard-vac">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Urlaub</div>
          <div className="text-xl font-mono font-bold text-emerald-300 mt-0.5">
            {v.remaining.toFixed(1)}<span className="text-[10px] text-zinc-500 ml-0.5">T</span>
          </div>
          {v.carry_over_total > 0 && (
            <div className="text-[10px] text-indigo-300">+{v.carry_over_total.toFixed(1)} Vorjahr</div>
          )}
        </div>
        <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2" data-testid="fahrer-dashboard-ot">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Überstd.</div>
          <div className={`text-xl font-mono font-bold mt-0.5 ${otNeg ? "text-rose-300" : "text-blue-300"}`}>
            {ot.balance_hours.toFixed(1)}<span className="text-[10px] text-zinc-500 ml-0.5">h</span>
          </div>
        </div>
        <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2" data-testid="fahrer-dashboard-sick">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Krank YTD</div>
          <div className="text-xl font-mono font-bold text-rose-300 mt-0.5">
            {data.sick.days_this_year}<span className="text-[10px] text-zinc-500 ml-0.5">d</span>
          </div>
        </div>
      </div>
      {data.pending_requests > 0 && (
        <div className="mt-3 text-[11px] text-amber-300 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          {data.pending_requests} Antrag{data.pending_requests !== 1 ? "e" : ""} warten auf Antwort
        </div>
      )}
      {v.carry_over_expiring_90d_days > 0 && (
        <div className="mt-1.5 text-[11px] text-amber-300 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          {v.carry_over_expiring_90d_days.toFixed(1)} Tage Resturlaub laufen in 90 Tagen ab
        </div>
      )}
    </Link>
  );
}

